import { useState } from "react";
import { Link } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { useNavigate } from "react-router-dom";
import { FileText, Plus, ArrowRight, Trash, Sparkle } from "@phosphor-icons/react";
import { useEffect } from "react";

export default function InvestmentDocsList() {
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ company_name: "", template: "cbc_institutional" });
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [l, c] = await Promise.all([
        api.get("/investment-docs"),
        api.get("/investment-docs/config"),
      ]);
      setItems(l.data || []);
      setConfig(c.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.company_name.trim()) return toast.error("Company name is required");
    try {
      const { data } = await api.post("/investment-docs", form);
      toast.success(`Created — ${data.company_name}`);
      navigate(`/investment-docs/${data.id}`);
    } catch (err) {
      toast.error(extractApiError(err, "Could not create"));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this investment documentation?")) return;
    try { await api.delete(`/investment-docs/${id}`); await load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10" data-testid="investment-docs-list-page">
      <PageHeader
        eyebrow="AI Investment Documentation"
        title="Institutional fundraising, on demand."
        subtitle="Turn your Investment Profile into a full investor package — pitch deck, memo, DD, one-pager, Q&A — in under five minutes."
        action={
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors card-elev"
            data-testid="idocs-create-btn"
          >
            <Plus size={14} weight="bold" /> New investment doc
          </button>
        }
      />

      {loading && <div className="mt-10 text-sm text-muted-foreground">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="mt-10 bg-card border border-border rounded-2xl p-10 sm:p-14 text-center card-elev" data-testid="idocs-empty">
          <div className="mx-auto h-14 w-14 rounded-full bg-teal-soft text-teal grid place-items-center mb-4">
            <FileText size={28} weight="duotone" />
          </div>
          <div className="font-display text-2xl tracking-tight mb-2">Start your first investor package</div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Complete the Investment Profile once, and Orixa Invest generates a complete institutional-grade fundraising package instantly.
          </p>
          <button
            onClick={() => setShowCreate(true)}
            className="mt-6 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-3 text-sm font-semibold hover:bg-primary/90 transition-colors card-elev"
            data-testid="idocs-empty-create"
          >
            <Sparkle size={14} weight="bold" /> Create investor package
          </button>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} className="bg-card border border-border rounded-2xl p-5 card-elev hover:card-elev-lg transition-shadow group" data-testid={`idoc-card-${it.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{it.template.replace(/_/g, " ")}</div>
                  <div className="font-display text-lg tracking-tight mt-1">{it.company_name}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                  it.status === "analysed" ? "bg-teal-soft text-teal" : "bg-secondary text-muted-foreground"
                }`}>{it.status}</span>
              </div>
              <div className="mt-4 text-[11px] text-muted-foreground">
                Updated {new Date(it.updated_at).toLocaleDateString()}
              </div>
              <div className="mt-4 flex items-center justify-between">
                <Link to={`/investment-docs/${it.id}`} className="text-sm font-semibold text-primary inline-flex items-center gap-1 group-hover:gap-2 transition-all" data-testid={`idoc-open-${it.id}`}>
                  Open <ArrowRight size={12} weight="bold" />
                </Link>
                <button onClick={() => remove(it.id)} className="text-xs text-muted-foreground hover:text-destructive" data-testid={`idoc-delete-${it.id}`}>
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center px-4" onClick={() => setShowCreate(false)} data-testid="idocs-create-modal">
          <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 max-w-lg w-full card-elev-lg" onClick={(e) => e.stopPropagation()}>
            <div className="text-overline text-primary mb-2">New investment doc</div>
            <div className="font-display text-2xl tracking-tight mb-6">Give your investor package a name.</div>
            <label className="text-overline text-muted-foreground block mb-2">Company name</label>
            <input
              value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              placeholder="e.g. AtlasPay Financial Services"
              className="w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm mb-4"
              data-testid="idocs-create-company"
              autoFocus
            />
            <label className="text-overline text-muted-foreground block mb-2">Template</label>
            <select
              value={form.template}
              onChange={(e) => setForm({ ...form, template: e.target.value })}
              className="w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm mb-6"
              data-testid="idocs-create-template"
            >
              {(config?.templates || []).map((t) => (
                <option key={t.key} value={t.key}>{t.label}{t.recommended ? " · Recommended" : ""}</option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowCreate(false)} className="text-sm text-muted-foreground hover:text-foreground px-4 py-2">Cancel</button>
              <button onClick={create} className="bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 card-elev inline-flex items-center gap-2" data-testid="idocs-create-submit">
                Create <ArrowRight size={12} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
